"use client";

import { useEffect } from "react";

import { reportError } from "@/components/ClientErrorReporter";

/**
 * Catches errors in the root layout. Must define its own <html> and <body>
 * because the root layout may have failed to render.
 *
 * Reporting matters MORE here than in any other boundary: this is the most
 * catastrophic failure the app has (the entire shell is gone, on every route
 * at once), and it used to be the only boundary that logged nothing — the
 * error prop was even named `_error` to mark it deliberately unused. A total
 * outage was therefore invisible in /admin/errors.
 *
 * `ClientErrorReporter` is mounted by the root layout, which by definition has
 * failed here, so its window listeners may never have been registered — we
 * call the exported `reportError` helper directly rather than relying on them.
 */
export default function GlobalError({
  error,
  reset,
}: {
  error: Error & { digest?: string };
  reset: () => void;
}) {
  useEffect(() => {
    reportError({
      source: "react-boundary",
      // Distinct from a route-segment boundary: this one means the whole shell
      // is gone on every route at once, which is a different severity of event
      // even though both report as `source: "react-boundary"`.
      trigger: "global-error-boundary",
      message: error?.message || "Root layout render error",
      stack: error?.stack ?? null,
      context: {
        boundary: "global-error",
        ...(error?.digest ? { digest: error.digest } : {}),
      },
    });
  }, [error]);

  return (
    <html lang="en">
      <body className="flex min-h-screen flex-col items-center justify-center bg-[#f6f4ee] px-6 font-sans antialiased">
        <span
          className="text-7xl sm:text-8xl"
          style={{ fontFamily: "cursive", color: "rgba(220,38,38,0.2)" }}
        >
          Oops
        </span>
        <h1
          className="mt-4 text-2xl font-semibold sm:text-3xl"
          style={{ fontFamily: "serif", color: "#2f3a33" }}
        >
          Something went wrong
        </h1>
        <p
          className="mt-3 max-w-sm text-center text-sm leading-relaxed sm:text-base"
          style={{ color: "#5e6a61" }}
        >
          An unexpected error occurred. Please try again.
        </p>
        <button
          type="button"
          onClick={reset}
          className="mt-8 inline-flex items-center rounded-lg px-6 py-3 text-sm font-medium text-white transition-colors hover:opacity-90"
          style={{ backgroundColor: "#6b7e6b" }}
        >
          <svg
            className="mr-2 h-4 w-4"
            fill="none"
            stroke="currentColor"
            strokeWidth={2}
            viewBox="0 0 24 24"
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M1 4v6h6M23 20v-6h-6M20.49 9A9 9 0 0 0 5.64 5.64L1 10m22 4-4.64 4.36A9 9 0 0 1 3.51 15"
            />
          </svg>
          Try again
        </button>
        {/* Plain <a> (hard navigation) is intentional here: this is the global
            error boundary, which renders its own <html>/<body> when the root
            layout has failed — next/link's router context may be unavailable,
            so a full reload is the reliable way back to the homepage. */}
        {/* eslint-disable-next-line @next/next/no-html-link-for-pages */}
        <a
          href="/"
          className="mt-4 text-sm underline transition-colors"
          style={{ color: "#6b7e6b" }}
        >
          Go to homepage
        </a>
      </body>
    </html>
  );
}
